import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Upload, Eye, Trash2, FileText, X } from 'lucide-react';
import AppLayout from '../components/AppLayout';
import { homeOwnersGuideService, HomeOwnersGuide } from '../services/homeOwnersGuideService';
import { projectService } from '../services/projectService';
import { Project } from '../types/project';
import { toast } from 'react-toastify';
import '../styles/Builders.css';
import '../styles/Buttons.css';
import '../styles/DataTable.css';

const HomeOwners: React.FC = () => {
  const navigate = useNavigate();
  const { workspaceId } = useParams<{ workspaceId: string }>();

  const [guides, setGuides] = useState<HomeOwnersGuide[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedProjects, setSelectedProjects] = useState<string[]>([]);
  const [viewingGuide, setViewingGuide] = useState<HomeOwnersGuide | null>(null);

  const loadData = useCallback(async () => {
    if (!workspaceId) return;

    try {
      setLoading(true);
      const [guidesData, projectsData] = await Promise.all([
        homeOwnersGuideService.getAll(workspaceId),
        projectService.getAll(workspaceId)
      ]);
      setGuides(guidesData.guides);
      setProjects(projectsData.projects || []);
    } catch (err: any) {
      console.error('Error loading data:', err);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleBackToWorkspaces = () => {
    navigate('/workspaces');
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Only PDF and DOC/DOCX files are allowed');
        return;
      }

      // Validate file size (50MB)
      if (file.size > 50 * 1024 * 1024) {
        toast.error('File size must be less than 50MB');
        return;
      }

      setSelectedFile(file);
    }
  };

  const handleProjectToggle = (projectId: string) => {
    setSelectedProjects(prev => {
      if (prev.includes(projectId)) {
        return prev.filter(id => id !== projectId);
      } else {
        return [...prev, projectId];
      }
    });
  };

  const handleUpload = async () => {
    if (!workspaceId || !selectedFile) {
      toast.error('Please select a file');
      return;
    }

    if (selectedProjects.length === 0) {
      toast.error('Please select at least one project');
      return;
    }

    try {
      setUploading(true);
      await homeOwnersGuideService.upload(workspaceId, selectedFile, selectedProjects);
      toast.success('File uploaded successfully');

      // Reset form
      setSelectedFile(null);
      setSelectedProjects([]);

      // Reload data
      await loadData();
    } catch (err: any) {
      console.error('Error uploading file:', err);
      toast.error(err.response?.data?.message || 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleView = (guide: HomeOwnersGuide) => {
    setViewingGuide(guide);
  };

  const handleCloseModal = () => {
    setViewingGuide(null);
  };

  const getViewerUrl = (guide: HomeOwnersGuide): string => {
    if (!guide.fileUrl) return '';

    // For PDF files, use the direct URL in iframe
    if (guide.mimeType === 'application/pdf') {
      return guide.fileUrl;
    }

    // For DOC/DOCX files, use Google Docs Viewer
    return `https://docs.google.com/viewer?url=${encodeURIComponent(guide.fileUrl)}&embedded=true`;
  };

  const handleDelete = async (guideId: string) => {
    if (!workspaceId) return;

    if (!window.confirm('Are you sure you want to delete this file?')) {
      return;
    }

    try {
      await homeOwnersGuideService.delete(workspaceId, guideId);
      toast.success('File deleted successfully');
      await loadData();
    } catch (err: any) {
      console.error('Error deleting file:', err);
      toast.error(err.response?.data?.message || 'Failed to delete file');
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getProjectNames = (projectIds: string[]): string => {
    if (!projectIds || projectIds.length === 0) return 'No projects';
    const names = projectIds
      .map(id => projects.find(p => p._id === id)?.name)
      .filter(Boolean);
    return names.length > 0 ? names.join(', ') : 'No projects';
  };

  return (
    <AppLayout onBack={handleBackToWorkspaces} backLabel="Workspaces">
      <div className="builders-container">
        {/* Section Header */}
        <div className="builders-section-header">
          <div>
            <h2 className="builders-section-title">Home Owners Guide</h2>
            <p className="builders-section-subtitle">
              Manage home owners guide documents and resources
            </p>
          </div>
        </div>

        {/* Upload Section - White Card */}
        <div style={{
          backgroundColor: '#FFFFFF',
          borderRadius: '12px',
          padding: '24px',
          boxShadow: '0 4px 12px rgba(72, 24, 47, 0.15), 0 2px 4px rgba(72, 24, 47, 0.08)',
          marginBottom: '24px'
        }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '600',
            margin: '0 0 20px 0',
            color: '#48182F'
          }}>
            Upload New Guide
          </h3>

          {/* File Upload */}
          <div style={{ marginBottom: '20px' }}>
            <label className="form-label" style={{ marginBottom: '8px', display: 'block' }}>
              Select File (PDF or DOC/DOCX)
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <input
                type="file"
                accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
                id="file-upload"
              />
              <label
                htmlFor="file-upload"
                className="btn btn-secondary btn-md"
                style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                <Upload size={18} />
                Choose File
              </label>
              {selectedFile && (
                <span style={{ color: '#6B7280', fontSize: '14px' }}>
                  {selectedFile.name} ({formatFileSize(selectedFile.size)})
                </span>
              )}
            </div>
          </div>

          {/* Project Selection */}
          <div style={{ marginBottom: '20px' }}>
            <label className="form-label" style={{ marginBottom: '12px', display: 'block' }}>
              Select Projects
            </label>
            {loading ? (
              <div style={{ color: '#6B7280' }}>Loading projects...</div>
            ) : projects.length === 0 ? (
              <div style={{ color: '#6B7280' }}>No projects available</div>
            ) : (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                gap: '12px',
                maxHeight: '200px',
                overflowY: 'auto',
                padding: '12px',
                backgroundColor: '#F5F3F0',
                borderRadius: '8px',
                border: '1px solid #D4CBC4'
              }}>
                {projects.map((project) => (
                  <label
                    key={project._id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      cursor: 'pointer',
                      padding: '8px',
                      borderRadius: '6px',
                      backgroundColor: selectedProjects.includes(project._id) ? '#48182F15' : 'transparent'
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedProjects.includes(project._id)}
                      onChange={() => handleProjectToggle(project._id)}
                      style={{ cursor: 'pointer' }}
                    />
                    <span style={{ fontSize: '14px' }}>{project.name}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Upload Button */}
          <button
            className="btn btn-primary btn-md"
            onClick={handleUpload}
            disabled={!selectedFile || selectedProjects.length === 0 || uploading}
            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            <Upload size={18} />
            {uploading ? 'Uploading...' : 'Upload Guide'}
          </button>
        </div>

        {/* Files List - White Card */}
        <div style={{
          backgroundColor: '#FFFFFF',
          borderRadius: '12px',
          padding: '24px',
          boxShadow: '0 4px 12px rgba(72, 24, 47, 0.15), 0 2px 4px rgba(72, 24, 47, 0.08)'
        }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '600',
            margin: '0 0 20px 0',
            color: '#48182F'
          }}>
            Uploaded Guides
          </h3>

          {loading ? (
            <div className="builders-loading">
              <div className="builders-loading-spinner" />
            </div>
          ) : guides.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '40px',
              color: '#6B7280',
              backgroundColor: '#F5F3F0',
              borderRadius: '8px'
            }}>
              <FileText size={48} color="#D4CBC4" style={{ margin: '0 auto 16px' }} />
              <p>No guides uploaded yet</p>
              <p style={{ fontSize: '14px', marginTop: '8px' }}>
                Upload your first home owners guide document above
              </p>
            </div>
          ) : (
            <div className="data-table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>File Name</th>
                    <th>Size</th>
                    <th>Projects</th>
                    <th>Uploaded</th>
                    <th style={{ width: '120px', textAlign: 'center' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {guides.map((guide, index) => (
                    <tr
                      key={guide._id}
                      style={{
                        backgroundColor: index % 2 === 0 ? '#FFFFFF' : '#F5F3F0'
                      }}
                    >
                      <td className="data-table-primary-cell">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <FileText size={18} color="#48182F" />
                          {guide.fileName}
                        </div>
                      </td>
                      <td>{formatFileSize(guide.fileSize)}</td>
                      <td className="data-table-secondary-cell">
                        {getProjectNames(guide.projects)}
                      </td>
                      <td>{new Date(guide.createdAt).toLocaleDateString()}</td>
                      <td>
                        <div style={{
                          display: 'flex',
                          gap: '8px',
                          justifyContent: 'center',
                          alignItems: 'center'
                        }}>
                          <button
                            onClick={() => handleView(guide)}
                            className="btn btn-secondary btn-sm"
                            style={{
                              padding: '6px 12px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}
                            title="View"
                          >
                            <Eye size={16} />
                            View
                          </button>
                          <button
                            onClick={() => handleDelete(guide._id)}
                            className="btn btn-secondary btn-sm"
                            style={{
                              padding: '6px 12px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              backgroundColor: '#EF444415',
                              color: '#EF4444',
                              border: '1px solid #EF4444'
                            }}
                            title="Delete"
                          >
                            <Trash2 size={16} />
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Document Viewer Modal */}
      {viewingGuide && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
          onClick={handleCloseModal}
        >
          <div
            style={{
              backgroundColor: '#FFFFFF',
              borderRadius: '12px',
              width: '90%',
              height: '90%',
              maxWidth: '1200px',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div
              style={{
                padding: '20px 24px',
                borderBottom: '1px solid #E5E7EB',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                backgroundColor: '#F5F3F0'
              }}
            >
              <div>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#48182F' }}>
                  {viewingGuide.fileName}
                </h3>
                <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#6B7280' }}>
                  {formatFileSize(viewingGuide.fileSize)}
                </p>
              </div>
              <button
                onClick={handleCloseModal}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '6px',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#E5E7EB'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <X size={24} color="#48182F" />
              </button>
            </div>

            {/* Modal Body - Document Viewer */}
            <div style={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
              <iframe
                src={getViewerUrl(viewingGuide)}
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none'
                }}
                title={viewingGuide.fileName}
              />
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
};

export default HomeOwners;
